import {
  ActivityModel,
  ActivityValue,
  FlatActivityModel,
  ValidationStatus
} from 'types/shared'
import { AllowedLocales, isValidDateInterval, isValidDateTime } from './date'
import { validateUsingCallback } from 'modules/interaction/utils/validateUsingCallback'
import { VALIDATION_STATUSES } from './constants'

const hasValidationCallback = (
  activityModel: Pick<ActivityModel, 'validation_activity_callback'>
) => !!activityModel.validation_activity_callback

type ErrorMatcherContext = {
  lang: AllowedLocales
}

type ErrorMatcher = {
  message: string
  matcher: (
    value: NonNullable<ActivityModel['value']>,
    activityModel: ActivityModel | FlatActivityModel,
    context: ErrorMatcherContext
  ) => boolean
}

const emptinessErrorMatcher: ErrorMatcher = {
  message: 'activity-input.validation.required-field',
  matcher: (value, activityModel) => {
    const isAutocompleteMultiselection =
      activityModel.content_type === 'autocomplete_multiselection'
    const isEmptyValue =
      value === '' ||
      (activityModel.information_request_callback &&
        typeof value !== 'object' &&
        !isAutocompleteMultiselection)

    return Boolean(activityModel.required && isEmptyValue)
  }
}

const regexErrorMatcher: ErrorMatcher = {
  message: 'activity-input.validation.wrong_format',
  matcher: (value, activityModel) =>
    Boolean(
      activityModel.content_type !== 'date' &&
        activityModel.content_type !== 'datetime' &&
        'validation_regex' in activityModel &&
        activityModel.validation_regex &&
        value !== '' &&
        !new RegExp(activityModel.validation_regex).test(value.toString())
    )
}

const autocompleteErrorMatcher: ErrorMatcher = {
  message: 'activity-input.validation.required-field',
  matcher: (value, activityModel) => {
    const isString = activityModel.content_type === 'string'
    const hasFilteraction = !!activityModel.information_request_filteraction

    const isAutocomplete =
      isString && (hasValidationCallback(activityModel) || hasFilteraction)

    return Boolean(
      isAutocomplete &&
        activityModel.required &&
        (typeof value === 'string' || (!value.id && !value.des))
    )
  }
}

const dateErrorMatcher: ErrorMatcher = {
  message: 'activity-input.validation.wrong_format',
  matcher: (value, activityModel, { lang }) =>
    (activityModel.content_type === 'date' ||
      activityModel.content_type === 'datetime') &&
    !isValidDateTime({
      dateTime: typeof value === 'string' ? value : '',
      minYear: activityModel.minimum_year,
      maxYear: activityModel.maximum_year,
      contentType: activityModel.content_type,
      lang
    })
}

const dateIntervalErrorMatcher: ErrorMatcher = {
  message: 'activity-input.validation.invalid-range',
  matcher: (value, activityModel, { lang }) =>
    (activityModel.content_type === 'date' ||
      activityModel.content_type === 'datetime') &&
    !isValidDateInterval({
      dateTime: typeof value === 'string' ? value : '',
      minDate: activityModel.valid_min_date,
      maxDate: activityModel.valid_max_date,
      lang
    })
}

const documentErrorMatcher: ErrorMatcher = {
  message: 'activity-input.validation.required-field',
  matcher: (value, activityModel) => {
    let parsedValue: { file?: string } = {
      file: ''
    }
    try {
      parsedValue = JSON.parse(
        typeof value === 'string' ? value : '{ "file": "" }'
      )
    } catch (e) {}

    return Boolean(
      activityModel.content_type === 'document_photo' &&
        !parsedValue.file &&
        activityModel.required
    )
  }
}

const otpErrorMatcher: ErrorMatcher = {
  message: 'activity-input.validation.required-field',
  matcher: (value, activityModel) =>
    activityModel.content_type === 'one_time_password' &&
    (typeof value === 'object' ? value.des : value)?.replaceAll(' ', '')
      ?.length !== activityModel.length
}

const agreementTermErrorMatcher: ErrorMatcher = {
  message: 'activity-input.validation.required-field',
  matcher: (value, activityModel) => {
    const isAgreementTerm = activityModel.content_type === 'agreement_term'

    if (!isAgreementTerm) return false

    const isAgreementTermChecked =
      typeof value === 'string' && JSON.parse(value)?.checked === '1'

    return isAgreementTerm && !isAgreementTermChecked
  }
}

const drawErrorMatcher: ErrorMatcher = {
  message: 'activity-input.validation.required-field',
  matcher: (value, activityModel) => {
    const isDraw = activityModel.content_type === 'draw'

    if (!isDraw) return false

    const isDrawChecked =
      typeof value === 'string' && !!JSON.parse(value)?.base64

    return isDraw && !isDrawChecked
  }
}

const errorMatchers = [
  emptinessErrorMatcher,
  regexErrorMatcher,
  autocompleteErrorMatcher,
  dateErrorMatcher,
  dateIntervalErrorMatcher,
  documentErrorMatcher,
  otpErrorMatcher,
  agreementTermErrorMatcher,
  drawErrorMatcher
]

export type ValidateFlatActivityValueProps = {
  context: ErrorMatcherContext
  activityModel: FlatActivityModel
  value?: ActivityValue
}

export const validateFlatActivityValue = async ({
  context,
  value,
  activityModel
}: ValidateFlatActivityValueProps): Promise<{
  validationStatus: ValidationStatus
  helperText: string
}> => {
  if (value === undefined) {
    return { validationStatus: VALIDATION_STATUSES.PENDING, helperText: '' }
  }

  const errorMessage = errorMatchers.find(({ matcher }) =>
    matcher(value, activityModel, context)
  )?.message

  if (errorMessage) {
    return {
      validationStatus: VALIDATION_STATUSES.INVALID,
      helperText: errorMessage
    }
  }

  const shouldCallValidationCallback =
    hasValidationCallback(activityModel) && value !== ''
  const isEmptyOptionalValue = value === '' && !activityModel.required

  if (!shouldCallValidationCallback && !isEmptyOptionalValue) {
    return {
      validationStatus: VALIDATION_STATUSES.VALID,
      helperText: 'activity-input.validation.is_valid'
    }
  }

  if (shouldCallValidationCallback) {
    return validateUsingCallback({ ...activityModel, value })
  }

  return { validationStatus: 'pending', helperText: '' }
}
